/**
 * UsageBadge — small pill rendered below assistant messages showing token
 * usage + cost in INR + the models that were used.
 *
 * Data shape (from /api/v1/aiaccountant/agent `usage` field):
 *   {
 *     input_tokens, output_tokens, cache_read_tokens, cache_write_tokens,
 *     cost_usd_raw, cost_inr_charged, cost_inr_raw, balance_after,
 *     fx_rate, markup, models_used: string[]
 *   }
 *
 * Markup is applied server-side (axon-billing); we just display what was
 * charged. fx_rate is fetched dynamically from frankfurter.app (ECB-backed).
 */

import { Coins, Cpu } from 'lucide-react';

export interface UsageStats {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_tokens?: number;
  cache_write_tokens?: number;
  cost_usd_raw?: number;
  cost_inr_raw?: number;
  cost_inr_charged?: number;
  balance_after?: number;
  fx_rate?: number;
  markup?: number;
  models_used?: string[];
  duration_ms?: number;
}

interface UsageBadgeProps {
  usage: UsageStats;
}

export function UsageBadge({ usage }: UsageBadgeProps) {
  const total = (usage.input_tokens ?? 0) + (usage.output_tokens ?? 0);
  const inr = usage.cost_inr_charged ?? null;
  const models = (usage.models_used ?? []).map(shortenModel);
  const fx = usage.fx_rate;
  const markup = usage.markup;

  if (total === 0 && inr == null) return null;

  return (
    <div className="mt-2 -mx-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground/90">
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted"
        title={`Input ${fmt(usage.input_tokens)} · Output ${fmt(usage.output_tokens)} · Cache-read ${fmt(usage.cache_read_tokens)} · Cache-write ${fmt(usage.cache_write_tokens)}`}
      >
        <Coins className="w-2.5 h-2.5" />
        {fmt(total)} tokens
      </span>

      {inr != null && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted"
          title={
            `Raw cost $${(usage.cost_usd_raw ?? 0).toFixed(6)}` +
            (fx ? ` · FX ₹${fx.toFixed(2)}/$` : '') +
            (markup ? ` · ${markup}× markup applied` : '')
          }
        >
          ₹{inr.toFixed(2)}
        </span>
      )}

      {models.length > 0 && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted"
          title={`Models used: ${(usage.models_used ?? []).join(', ')}`}
        >
          <Cpu className="w-2.5 h-2.5" />
          {models.join(' · ')}
        </span>
      )}

      {usage.balance_after != null && (
        <span className="text-muted-foreground/60">
          balance ₹{usage.balance_after.toFixed(2)}
        </span>
      )}
    </div>
  );
}

function fmt(n?: number): string {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

/** "claude-sonnet-4-6" → "sonnet-4.6"; "gemini-3.1-pro-preview" → "gemini-3.1-pro" */
function shortenModel(model: string): string {
  return model
    .replace(/^claude-/, '')
    .replace(/-preview$/, '')
    .replace(/-(\d)-(\d)/, '-$1.$2');
}
